import { Suspense, lazy, useState, useEffect } from "react";
import { Pivot, PivotItem } from "@fluentui/react";
import DOMPurify from "dompurify";
import { ChevronDown24Regular, ChevronUp24Regular, Document24Regular, Dismiss24Regular, Open24Regular } from "@fluentui/react-icons";
import styles from "./AnalysisPanel.module.css";
import { AskResponse } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { getPage } from "../../utils/functions";
import { SourceModal } from "../SourceModal";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: AskResponse;
    fileType: string;
    isMobile?: boolean;
    onClose?: () => void;
}

import { formatStudyTitle } from "../../utils/formatters";

function formatPanelTitle(title: string): string {
    return formatStudyTitle(title);
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

interface Documento {
    id: string;
    title: string;
    year: string;
    subdireccion: string;
    categoria: string;
    filepaths: string[];
    sharepoint: string;
}

interface FragmentSource {
    title: string;
    rawTitle: string;
    content: string;
    filepath?: string;
}

interface GroupedSource {
    studyTitle: string;
    sharepointUrl?: string;
    fragments: FragmentSource[];
}

const CollapsibleSource = ({ content, title, onOpenModal }: {
    content: string;
    title: string;
    onOpenModal: () => void;
}) => {
    // Extraer texto limpio para el preview
    const getPreviewText = (html: string) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let text = doc.body.textContent || "";
        
        // Limpiar marcadores de sistema (ej: :unselected:)
        text = text.replace(/:unselected:/gi, '');
        
        // Limpiar puntos y espacios al inicio y al final para evitar ". Se prevén..."
        return text.trim().replace(/^[\.\s]+|[\.\s]+$/g, "");
    };

    const previewText = getPreviewText(content);

    return (
        <div className={styles.sourceCard} onClick={onOpenModal} title="Ver texto completo">
            <div className={styles.sourceHeader}>
                <div className={styles.sourceHeaderLeft}>
                    <Document24Regular className={styles.sourceIcon} />
                    <div className={styles.sourceTitleContainer}>
                        <span className={styles.sourceTitle}>{title}</span>
                        <div className={styles.sourcePreview}>
                            {previewText}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


export const AnalysisPanel = ({ answer, activeTab, activeCitation, className, onActiveTabChanged, fileType, isMobile, onClose }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.thoughts;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [selectedSource, setSelectedSource] = useState<FragmentSource | null>(null);
    const [documentos, setDocumentos] = useState<Documento[]>([]);

    useEffect(() => {
        fetch('/documentos.json')
            .then(response => response.json())
            .then((data: Documento[]) => setDocumentos(data))
            .catch(error => console.error('Error cargando documentos.json:', error));
    }, []);

    // Parsear data_points que viene como string con patrón Title:/Content:/URL:/Filepath:
    const parseDataPoints = (dataPointsString: string): FragmentSource[] => {
        // Limpiar :selected:
        const cleanedString = dataPointsString.replace(/:selected:/g, '');

        // Dividir por "Title:" para separar las Publicaciones/Estudios
        const sources = cleanedString.split(/(?=Title:)/g).filter(s => s.trim());

        return sources.map(source => {
            // Extraer título
            const titleMatch = source.match(/Title:\s*(.*?)(?=Content:|$)/s);
            const rawTitle = titleMatch ? titleMatch[1].trim() : 'Sin título';
            const title = formatPanelTitle(rawTitle);

            // Extraer contenido (todo entre Content: y URL: o Filepath:)
            const contentMatch = source.match(/Content:\s*(.*?)(?=URL:|Filepath:|$)/s);
            const content = contentMatch ? contentMatch[1].trim() : '';

            // Extraer filepath
            const filepathMatch = source.match(/Filepath:\s*(.*?)(?=\n|$)/s);
            const filepath = filepathMatch ? filepathMatch[1].trim() : undefined;

            return { title, rawTitle, content, filepath };
        }).filter(source => source.content); // Filtrar Publicaciones/Estudios sin contenido
    };

    const dataPointsString = typeof answer.data_points === 'string'
        ? answer.data_points
        : answer.data_points.toString();

    const flatSources = parseDataPoints(dataPointsString);
    
    // Agrupar por estudio
    const groupedSources: GroupedSource[] = [];
    flatSources.forEach(source => {
        const documento = documentos.find(doc =>
            doc.filepaths.some(fp => source.rawTitle.toLowerCase().includes(fp.toLowerCase()) || fp.toLowerCase().includes(source.rawTitle.toLowerCase()))
        );
        
        const studyTitle = documento ? formatPanelTitle(documento.title) : "Otros documentos";
        const sharepoint = documento?.sharepoint;
        
        let group = groupedSources.find(g => g.studyTitle === studyTitle);
        if (!group) {
            group = { studyTitle, sharepointUrl: sharepoint, fragments: [] };
            groupedSources.push(group);
        }
        group.fragments.push(source);
    });

    // Ordenar fragmentos dentro de cada estudio según el orden en `filepaths`
    groupedSources.forEach(group => {
        const study = documentos.find(d => formatPanelTitle(d.title) === group.studyTitle);
        if (study) {
            group.fragments.sort((a, b) => {
                const indexA = study.filepaths.findIndex(fp => a.rawTitle.toLowerCase().includes(fp.toLowerCase()) || fp.toLowerCase().includes(a.rawTitle.toLowerCase()));
                const indexB = study.filepaths.findIndex(fp => b.rawTitle.toLowerCase().includes(fp.toLowerCase()) || fp.toLowerCase().includes(b.rawTitle.toLowerCase()));
                return indexA - indexB;
            });
        }
    });

    const handleSharePointClick = (url: string, e: React.MouseEvent) => {
        e.stopPropagation();
        window.open(url, '_blank');
    };

    return (
        <div className={styles.analysisPanelContainer}>
            <div className={`${styles.thoughtProcess} ${styles.thoughtProcessMobile}`}>
                {groupedSources.length > 0 ? (
                    groupedSources.map((group, gIndex) => (
                        <div key={gIndex} className={styles.studyGroup}>
                            <div className={styles.studyHeader}>
                                <div className={styles.studyTitleContainer}>
                                    <h4 className={styles.studyGroupTitle}>{group.studyTitle}</h4>
                                    {group.sharepointUrl && (
                                        <button 
                                            className={styles.studySharePointButton} 
                                            onClick={(e) => handleSharePointClick(group.sharepointUrl!, e)}
                                            title="Ver documentos asociados"
                                            aria-label="Ver documentos asociados"
                                        >
                                            <Open24Regular />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.fragmentsList}>
                                {group.fragments.map((source, fIndex) => (
                                    <CollapsibleSource
                                        key={fIndex}
                                        content={source.content}
                                        title={source.title}
                                        onOpenModal={() => setSelectedSource(source)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={styles.noSources}>No hay Publicaciones/Estudios disponibles</div>
                )}
            </div>

            <SourceModal
                isOpen={selectedSource !== null}
                onClose={() => setSelectedSource(null)}
                title={selectedSource?.title || ''}
                content={selectedSource?.content || ''}
                filepath={selectedSource?.filepath}
            />
        </div>
    );
};
